import { HttpCancelError } from '@cian/peperrors';
import { IApplicationContext } from 'shared/types/applicationContext';

interface IConfiguration<R, P, S, F = void> {
  /** Параметры запроса */
  parameters: P;
  /** Дефолтное значение */
  defaultValue?: R;
  /** Домен - по сути название вызывающей функции */
  domain: string;

  /** Функция для запроса данных */
  fetchFunction: (args: P) => Promise<TFetchFunctionResponse<S, F>>;
  /** Обработчик 400 ошибки */
  errorHandler?: (domain: string, response: F) => Error;
  /** Функция для подготовки итоговых данных */
  prepareFunction?: (response: S) => R;
}

type TFetchFunctionResponse<S, F = void> = TSuccessResponse<S> | TFailureResponse<F>;

interface IResponse<R> {
  response: R;
}

type TSuccessResponse<S> = IResponse<S> & {
  statusCode: 200;
};

type TFailureResponse<F = void> = F extends void ? never : IFailureResponse<F>;

interface IFailureResponse<F> extends IResponse<F> {
  statusCode: 400;
}

export async function request<R, P, S, F = void>(
  { httpApi, logger }: IApplicationContext,
  { parameters, domain, defaultValue, fetchFunction, errorHandler, prepareFunction }: IConfiguration<R, P, S, F>,
): Promise<R> {
  try {
    const { response, statusCode } = await fetchFunction({
      httpApi,
      ...parameters,
    });

    if (statusCode === 200) {
      return prepareFunction ? prepareFunction(response) : (response as unknown as R);
    }

    if (errorHandler) {
      throw errorHandler(domain, response);
    }

    throw new Error('Unknown error');
  } catch (e) {
    if (e instanceof HttpCancelError) {
      throw e;
    }

    logger.error(e, {
      domain,
      parameters: JSON.stringify(parameters),
    });

    if (typeof defaultValue !== 'undefined') {
      return defaultValue;
    }

    throw e;
  }
}
